// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { Modal } from 'components/atoms/Modal';
import { useEffect, useMemo, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import { addition } from 'util/Calc';
import styles from './EmissionBatchEditModal.module.scss';
import { IconClose } from 'components/atoms/icons/IconClose';
import { createAxios } from 'ts/createAxios';
import { handleError } from 'util/errorHandler';
import { useTranslation } from 'react-i18next';
import { normalizeNumber } from 'util/commons';
import { WorkHistoriesEmission } from 'types/workHistoriesTypes';
import { EmissionBatchEditItem } from 'components/molecules/EmissionBatchEditItem';
import { ItemType, Tenant } from 'util/Types';
import Portal from 'components/atoms/Portal';
import { toast } from 'react-toastify';
import { SelectOption } from 'components/molecules/SelectField';
import { getTemporaryStorages } from 'apis/temporary_storage';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { API_ENDPOINTS } from 'util/endPoints';

export type EmissionEditModalEmissionData = WorkHistoriesEmission & {
  id: string;
  collectUserName?: string;
};

export type EmissionEditModalProps = {
  emissionData: EmissionEditModalEmissionData[];
  onClose: () => void;
  afterSubmit?: (type: 'changed' | 'unchanged') => void;
};

export const EmissionBatchEditModal = ({
  emissionData,
  onClose,
  afterSubmit,
}: EmissionEditModalProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<EmissionEditModalEmissionData[]>([]);
  const [temporaryStorageOptions, setTemporaryStorageOptions] = useState<
    SelectOption[]
  >([]);
  const [rawTenantOptions, setRawTenantOptions] = useState<SelectOption[]>([]);
  const user = useSelector(selectUser);

  const isDisabled = useMemo(() => {
    return data.some((value) =>
      value?.itemType === ItemType.FIX ? !value.quantity : !value.weight
    );
  }, [data]);

  useEffect(() => {
    if (user.temporaryStorageEnabled) {
      getTemporaryStorages({
        successCallback: (data) => {
          setTemporaryStorageOptions(
            data.map((item) => ({
              label: item.name,
              value: item.id,
            }))
          );
        },
      });
    } else {
      setTemporaryStorageOptions([]);
    }

    createAxios()
      .get<{
        tenants: Tenant[];
      }>(API_ENDPOINTS.TENANTS, {
        params: {
          siteId: user.siteId,
        },
      })
      .then(({ data }) => {
        setRawTenantOptions(
          data.tenants.map((tenant) => ({
            label: tenant.name,
            value: tenant.id,
          }))
        );
      })
      .catch((error) => {
        handleError(error);
      });
  }, [user]);

  const tenantOptions = useMemo(() => {
    const defaultOption: SelectOption = {
      label: t('common.placeholder.no_tenant'),
      value: -1,
    };

    return [defaultOption, ...rawTenantOptions];
  }, [rawTenantOptions, t]);

  useEffect(() => {
    setData(emissionData);
  }, [emissionData]);

  const totalWeight = useMemo(() => {
    const quantities: Array<number> = data.map((emission) => {
      const { quantity, convertKg, weight } = emission;

      if (quantity && convertKg) {
        return Number(quantity) * Number(convertKg);
      }

      if (weight && emission?.itemType === ItemType.OPTIONAL) {
        return Number(weight);
      }

      return 0;
    });

    return quantities.reduce((sum, quantity) => addition(sum, quantity), 0);
  }, [data]);

  const handleChangeInput = (id: string, field: string, value: string) => {
    const newDataChanged = data.map((item) => {
      return item.id === id
        ? {
            ...item,
            ...(field === 'wasteUnit'
              ? {
                  [item?.itemType === ItemType.OPTIONAL
                    ? 'weight'
                    : 'quantity']: value,
                }
              : field === 'temporaryStorageId'
              ? {
                  temporaryStorage: {
                    id: Number(value),
                    name: '',
                  },
                }
              : {
                  [field]: value,
                }),
          }
        : item;
    });

    setData(newDataChanged);
  };

  const callAPIUpdateEmission = async () => {
    const body = data
      .filter((n) => {
        const oldItem = emissionData.find((e) => e.id === n.id);

        return (
          oldItem &&
          (oldItem.weight !== n.weight ||
            oldItem.quantity !== n.quantity ||
            oldItem.temporaryStorage?.id !== n.temporaryStorage?.id ||
            oldItem.datetime !== n.datetime ||
            oldItem.tenantId !== n.tenantId)
        );
      })
      .map((data) => {
        return {
          emissionId: data.id,
          quantity: Number(
            Math.round(Number(data.quantity || data.weight) * 100000) / 100000
          ),
          temporaryStorageId: data.temporaryStorage?.id,
          emissionDatetime: data.datetime,
          tenantId: data.tenantId,
        };
      });

    if (body.length > 0) {
      try {
        await createAxios({
          prefix: 'v2',
        }).put(API_ENDPOINTS.EMISSIONS, {
          waste: body,
        });

        toast.success(t('messages.M_011'));
        onClose();
        afterSubmit?.('changed');
      } catch (error) {
        handleError(error);
      }
    } else {
      toast.success(t('messages.M_011'));
      onClose();
      afterSubmit?.('unchanged');
    }
  };

  return (
    <Portal>
      <section className={styles.downloadModal}>
        <Modal>
          <div className={styles.customTitle}>
            <div className={styles.title}>
              {t('un_collect.edit_registration_data')}
            </div>
            <div className={styles.top} onClick={onClose}>
              <IconClose />
            </div>
          </div>

          <div className={styles.totalRecovery}>
            <p>
              {t('un_collect.total_recovery')}&nbsp;
              <span>{normalizeNumber({ value: totalWeight })}kg</span>
            </p>
          </div>

          <div className={styles.body}>
            <div className={styles.emissionEditItem}>
              <div className={styles.emissionEditItemCol}>
                <p className={styles.itemName}>{t('un_collect.thead_item')}</p>
              </div>
              <div className={styles.emissionEditItemCol}>
                <p className={styles.itemName}>
                  {t('un_collect.thead_discharged_date_and_time')}
                </p>
              </div>
              <div className={styles.emissionEditItemCol}>
                <p className={styles.itemName}>
                  {t('un_collect.thead_tenant')}
                </p>
              </div>
              {user.temporaryStorageEnabled && (
                <div className={styles.emissionEditItemCol}>
                  <p className={styles.itemName}>
                    {t('un_collect.thead_storage_location')}
                  </p>
                </div>
              )}
              <div className={styles.emissionEditItemCol}>
                <p className={styles.itemName}>
                  {t('un_collect.thead_weight')}
                </p>
              </div>
            </div>
            {data.map((emissionItem) => {
              return (
                <EmissionBatchEditItem
                  key={emissionItem.id}
                  emissionEditData={emissionItem}
                  onChange={handleChangeInput}
                  temporaryStorageOptions={temporaryStorageOptions}
                  tenantOptions={tenantOptions}
                />
              );
            })}
          </div>

          <div className={styles.submit}>
            <button
              className={styles.submitButton}
              type="submit"
              disabled={isDisabled}
              onClick={() => {
                callAPIUpdateEmission();
              }}
            >
              {t('common.button.reflect_the_correction')}
            </button>
          </div>
        </Modal>
      </section>
    </Portal>
  );
};
